import { Form, Input, Modal, Switch } from 'antd'
import { FaqCategoryItem, FaqCategoryVisibility } from 'Modules'
import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import { AppAdmin } from 'api/app-admin'
import { useCreateFaqCategory, useUpdateFaqCategory } from 'hooks/faq'
import { getApiErrorMessage } from 'utils/api-error'

import style from './faq-category-form-modal.module.scss'

const NAME_MAX = 50

interface FaqCategoryFormModalProps {
  visible: boolean
  editingItem?: FaqCategoryItem
  onClose: () => void
  onSuccess: (item?: FaqCategoryItem) => void
}

export const FaqCategoryFormModal: React.FC<FaqCategoryFormModalProps> = ({
  visible,
  editingItem,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm<{ name: string }>()
  const [visibleOnWeb, setVisibleOnWeb] = useState(true)
  const [nameAvailable, setNameAvailable] = useState(true)
  const [nameCheckPending, setNameCheckPending] = useState(false)
  const createCategory = useCreateFaqCategory()
  const updateCategory = useUpdateFaqCategory()
  const isEdit = !!editingItem

  const nameValue = Form.useWatch('name', form)
  const trimmedName = useMemo(() => nameValue?.trim() ?? '', [nameValue])

  useEffect(() => {
    if (visible && editingItem) {
      form.setFieldsValue({ name: editingItem.name })
      setVisibleOnWeb(editingItem.visibility === 'VISIBLE')
    } else if (visible && !editingItem) {
      form.resetFields()
      setVisibleOnWeb(true)
    }
  }, [visible, editingItem, form])

  useEffect(() => {
    if (!visible) {
      return
    }
    if (!trimmedName) {
      setNameAvailable(false)
      setNameCheckPending(false)
      return
    }
    if (isEdit && editingItem && trimmedName === editingItem.name.trim()) {
      setNameAvailable(true)
      setNameCheckPending(false)
      return
    }

    let cancelled = false
    setNameCheckPending(true)
    const timer = window.setTimeout(async () => {
      try {
        const res = await AppAdmin.checkFaqCategoryName(trimmedName, editingItem?._id)
        if (!cancelled) {
          setNameAvailable(Boolean(res.data?.data?.available))
        }
      } catch {
        if (!cancelled) {
          setNameAvailable(false)
        }
      } finally {
        if (!cancelled) {
          setNameCheckPending(false)
        }
      }
    }, 300)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [visible, trimmedName, isEdit, editingItem])

  const canSubmit = trimmedName.length > 0 && nameAvailable && !nameCheckPending

  const handleClose = () => {
    form.resetFields()
    onClose()
  }

  const handleSubmit = async () => {
    if (!canSubmit) {
      return
    }
    try {
      const values = await form.validateFields()
      const visibility: FaqCategoryVisibility = visibleOnWeb ? 'VISIBLE' : 'HIDDEN'
      const payload = { name: values.name.trim(), visibility }

      if (isEdit && editingItem) {
        const updated = await updateCategory.mutateAsync({ id: editingItem._id, payload })
        toast.success('Save changes successfully')
        onSuccess(updated)
      } else {
        const created = await createCategory.mutateAsync(payload)
        toast.success('Create category successfully')
        onSuccess(created)
      }
      handleClose()
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        return
      }
      toast.error(getApiErrorMessage(error, 'Failed to save category'))
    }
  }

  return (
    <Modal
      open={visible}
      title={isEdit ? 'Edit Category' : 'Create Category'}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText={isEdit ? 'Save' : 'Create'}
      okButtonProps={{ disabled: !canSubmit }}
      confirmLoading={createCategory.isPending || updateCategory.isPending}
      destroyOnClose
      width={480}
    >
      <Form form={form} layout="vertical" className={style.form}>
        <Form.Item
          name="name"
          label="Category name"
          validateStatus={trimmedName && !nameCheckPending && !nameAvailable ? 'error' : undefined}
          help={
            trimmedName && !nameCheckPending && !nameAvailable
              ? 'Category name already exists'
              : undefined
          }
          rules={[
            { required: true, whitespace: true, message: 'Category name is required' },
            { max: NAME_MAX, message: `Category name must be at most ${NAME_MAX} characters` },
          ]}
        >
          <Input placeholder="Enter category name" maxLength={NAME_MAX} showCount />
        </Form.Item>
        <Form.Item label="Status">
          <div className={style.visibilityRow}>
            <Switch checked={visibleOnWeb} onChange={setVisibleOnWeb} />
            <span>{visibleOnWeb ? 'Visible' : 'Hidden'}</span>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  )
}
